import React, { useState } from 'react';
import axios from 'axios';
import t from '../theme';

const EXPORTS = [
  {
    key: 'students',
    icon: '👥',
    title: 'Student Roster',
    desc: 'All students with contact info, guardian details, and enrollment dates.',
    filename: 'students.csv',
    color: '#1c1b19',
    accent: '#c49a3c',
  },
  {
    key: 'progress',
    icon: '📊',
    title: 'Progress Report',
    desc: 'Each student\'s average scores per Taal and overall progress percentage.',
    filename: 'progress.csv',
    color: '#e8daef',
    accent: '#8e44ad',
  },
  {
    key: 'attendance',
    icon: '📅',
    title: 'Attendance Log',
    desc: 'Full attendance history for every session — date, student, present/absent.',
    filename: 'attendance.csv',
    color: '#d5f5e3',
    accent: '#27ae60',
  },
  {
    key: 'homework',
    icon: '📝',
    title: 'Homework Grades',
    desc: 'All assignments with student status, scores, and teacher feedback.',
    filename: 'homework.csv',
    color: '#fef9e7',
    accent: '#f39c12',
  },
];

export default function ExportPage() {
  const [downloading, setDownloading] = useState({});
  const [done, setDone] = useState({});

  const handleExport = async (key, filename) => {
    setDownloading(d => ({ ...d, [key]: true }));
    try {
      const res = await axios.get(`/api/export/${key}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setDone(d => ({ ...d, [key]: true }));
      setTimeout(() => setDone(d => ({ ...d, [key]: false })), 3000);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setDownloading(d => ({ ...d, [key]: false }));
    }
  };

  return (
    <div>
      <div style={s.topRow}>
        <div>
          <h2 style={s.heading}>Export Data</h2>
          <p style={s.subheading}>Download reports as CSV files — open in Excel or Google Sheets.</p>
        </div>
      </div>

      <div style={s.grid}>
        {EXPORTS.map(exp => (
          <div key={exp.key} style={{ ...s.card, borderTop: `4px solid ${exp.accent}` }}>
            <div style={{ ...s.cardIcon, background: exp.color }}>{exp.icon}</div>
            <div style={s.cardBody}>
              <h3 style={s.cardTitle}>{exp.title}</h3>
              <p style={s.cardDesc}>{exp.desc}</p>
              <div style={s.cardFooter}>
                <span style={s.filename}>📄 {exp.filename}</span>
                <button
                  style={{ ...s.downloadBtn, background: exp.accent, opacity: downloading[exp.key] ? 0.7 : 1 }}
                  onClick={() => handleExport(exp.key, exp.filename)}
                  disabled={downloading[exp.key]}>
                  {done[exp.key] ? '✓ Downloaded!' : downloading[exp.key] ? 'Preparing...' : '⬇ Download CSV'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={s.infoBox}>
        <strong>💡 Tip:</strong> CSV files open in Microsoft Excel, Google Sheets, or any spreadsheet app.
        To open in Excel: double-click the file. In Google Sheets: File → Import → Upload.
      </div>
    </div>
  );
}

const s = {
  topRow: { marginBottom: '24px' },
  heading: { fontSize: '24px', fontWeight: 800, color: '#f0ede8', margin: 0 },
  subheading: { fontSize: '14px', color: '#6b6966', margin: '4px 0 0' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', marginBottom: '24px' },
  card: { background: '#161614', borderRadius: '16px', border: '1px solid #2a2927', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.4)', display: 'flex', flexDirection: 'column' },
  cardIcon: { fontSize: '40px', padding: '24px', textAlign: 'center' },
  cardBody: { padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 },
  cardTitle: { fontSize: '17px', fontWeight: 800, color: '#f0ede8', margin: 0 },
  cardDesc: { fontSize: '13px', color: '#6b6966', margin: 0, lineHeight: 1.5, flex: 1 },
  cardFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginTop: '8px', flexWrap: 'wrap' },
  filename: { fontSize: '12px', color: '#4a4845', fontFamily: 'monospace' },
  downloadBtn: { color: '#161614', border: 'none', padding: '9px 18px', borderRadius: '10px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 3px 8px rgba(0,0,0,0.15)', whiteSpace: 'nowrap' },
  infoBox: { background: '#1c1b19', border: '1px solid #b8d9f0', borderRadius: '12px', padding: '14px 18px', fontSize: '13px', color: '#c49a3c', lineHeight: 1.6 },
};
